/**
 * PendingInvitationsList Component
 *
 * Displays a list of pending setter invitations for a gym.
 * Allows gym owners to resend or cancel invitations.
 */

import { useState, useEffect, useCallback, type CSSProperties } from 'react';
import { collection, query, where, onSnapshot, type DocumentData } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseDb, getFirebaseFunctions } from '@/lib/firebase';
import type { SetterInvitation, InvitationStatus, TimestampLike } from '@flash/shared';

/**
 * Helper to convert TimestampLike to Date
 */
const toDate = (timestamp: TimestampLike): Date => {
  if (timestamp instanceof Date) {
    return timestamp;
  }
  // Firestore Timestamp has toDate method
  if ('toDate' in timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  // Fallback: treat as serialized timestamp
  return new Date(timestamp as unknown as string);
};

/**
 * Props for PendingInvitationsList component
 */
interface PendingInvitationsListProps {
  /** ID of the gym */
  gymId: string;
  /** Callback when an invitation is resent */
  onResend?: (email: string) => void;
  /** Callback when an invitation is cancelled */
  onCancel?: (email: string) => void;
}

/**
 * Convert Firestore document to invitation with display info
 */
interface InvitationDisplay extends SetterInvitation {
  /** Whether the invitation is expired */
  isExpired: boolean;
  /** Days until expiration (or days since expiration if negative) */
  daysUntilExpiry: number;
  /** Formatted date string for when it was sent */
  sentDateDisplay: string;
  /** Formatted date string for expiration */
  expiresDateDisplay: string;
}

const docToInvitationDisplay = (docData: DocumentData): InvitationDisplay => {
  const createdAt = toDate(docData['createdAt'] as TimestampLike);
  const expiresAt = toDate(docData['expiresAt'] as TimestampLike);
  const now = new Date();

  const isExpired = now > expiresAt;
  const daysUntilExpiry = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  return {
    id: docData['id'] as string,
    gymId: docData['gymId'] as string,
    email: docData['email'] as string,
    invitedBy: docData['invitedBy'] as string,
    status: docData['status'] as InvitationStatus,
    token: docData['token'] as string,
    createdAt: docData['createdAt'],
    expiresAt: docData['expiresAt'],
    isExpired,
    daysUntilExpiry,
    sentDateDisplay: createdAt.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    expiresDateDisplay: expiresAt.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
  };
};

/**
 * List of pending invitations with management actions
 */
export const PendingInvitationsList = ({
  gymId,
  onResend,
  onCancel,
}: PendingInvitationsListProps) => {
  const [invitations, setInvitations] = useState<InvitationDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Subscribe to pending invitations
  useEffect(() => {
    const db = getFirebaseDb();
    const invitationsRef = collection(db, 'setterInvitations');
    const invitationsQuery = query(
      invitationsRef,
      where('gymId', '==', gymId),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(
      invitationsQuery,
      (snapshot) => {
        const invitationsList = snapshot.docs
          .map((doc) => docToInvitationDisplay(doc.data()))
          .sort((a, b) => {
            // Sort by creation date, newest first
            const dateA = toDate(a.createdAt);
            const dateB = toDate(b.createdAt);
            return dateB.getTime() - dateA.getTime();
          });

        setInvitations(invitationsList);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching invitations:', err);
        setError('Failed to load invitations');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [gymId]);

  /**
   * Resend an invitation (creates new token and extends expiration)
   */
  const handleResend = useCallback(
    async (invitation: InvitationDisplay) => {
      setActionLoading(invitation.id);
      setError(null);

      try {
        // We reuse inviteSetter which handles the logic
        // It will detect there's an expired invitation and create a new one
        const functions = getFirebaseFunctions();
        const inviteSetter = httpsCallable<
          { gymId: string; email: string },
          { success: boolean; invitationId: string }
        >(functions, 'inviteSetter');

        await inviteSetter({ gymId, email: invitation.email });
        onResend?.(invitation.email);
      } catch (err) {
        const error = err as { message?: string };
        setError(error.message ?? 'Failed to resend invitation');
      } finally {
        setActionLoading(null);
      }
    },
    [gymId, onResend]
  );

  /**
   * Cancel an invitation
   * Note: In this implementation, we don't have a cancel function
   * So we just mark it as not implemented or the owner can wait for expiry
   */
  const handleCancel = useCallback(
    async (invitation: InvitationDisplay) => {
      setActionLoading(invitation.id);
      setError(null);

      try {
        // For MVP, we just show a message that cancellation isn't available
        // A proper implementation would call a cancelInvitation function
        setError(
          'Invitation cancellation is not yet implemented. The invitation will expire automatically.'
        );
        onCancel?.(invitation.email);
      } catch (err) {
        const error = err as { message?: string };
        setError(error.message ?? 'Failed to cancel invitation');
      } finally {
        setActionLoading(null);
      }
    },
    [onCancel]
  );

  if (loading) {
    return <div style={loadingStyle}>Loading invitations...</div>;
  }

  if (invitations.length === 0) {
    return (
      <div style={emptyStateStyle}>
        <p style={emptyStateTitleStyle}>No pending invitations</p>
        <p style={emptyStateTextStyle}>Use the form above to invite setters to your gym.</p>
      </div>
    );
  }

  return (
    <div>
      {error && <div style={errorBannerStyle}>{error}</div>}

      <ul style={listStyle}>
        {invitations.map((invitation) => (
          <li
            key={invitation.id}
            style={{
              ...listItemStyle,
              ...(invitation.isExpired ? listItemExpiredStyle : {}),
            }}
          >
            <div style={listItemContentStyle}>
              <div>
                <p style={emailStyle}>{invitation.email}</p>
                <p style={dateStyle}>Sent: {invitation.sentDateDisplay}</p>
                <p
                  style={{
                    ...dateStyle,
                    ...(invitation.isExpired ? expiredTextStyle : {}),
                  }}
                >
                  {invitation.isExpired
                    ? `Expired: ${invitation.expiresDateDisplay}`
                    : `Expires: ${invitation.expiresDateDisplay} (${invitation.daysUntilExpiry} day${invitation.daysUntilExpiry !== 1 ? 's' : ''} left)`}
                </p>
              </div>

              <div style={actionsStyle}>
                <button
                  onClick={() => handleResend(invitation)}
                  disabled={actionLoading === invitation.id}
                  style={{
                    ...resendButtonStyle,
                    ...(actionLoading === invitation.id ? buttonDisabledStyle : {}),
                  }}
                >
                  {actionLoading === invitation.id ? 'Sending...' : 'Resend'}
                </button>
                <button
                  onClick={() => handleCancel(invitation)}
                  disabled={actionLoading === invitation.id}
                  style={{
                    ...cancelButtonStyle,
                    ...(actionLoading === invitation.id ? { opacity: 0.5 } : {}),
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>

            {invitation.isExpired && (
              <div style={expiredBannerStyle}>
                This invitation has expired. Click &quot;Resend&quot; to send a new one.
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Component styles using CSS variables for dark mode support
 */
const loadingStyle: CSSProperties = {
  padding: '24px',
  textAlign: 'center',
  color: 'var(--color-text-secondary)',
};

const emptyStateStyle: CSSProperties = {
  padding: '24px',
  textAlign: 'center',
  color: 'var(--color-text-secondary)',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
};

const emptyStateTitleStyle: CSSProperties = {
  margin: 0,
  color: 'var(--color-text-secondary)',
};

const emptyStateTextStyle: CSSProperties = {
  margin: '8px 0 0 0',
  fontSize: '14px',
  color: 'var(--color-text-muted)',
};

const errorBannerStyle: CSSProperties = {
  padding: '12px 16px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  borderRadius: '8px',
  marginBottom: '16px',
  color: 'var(--color-error)',
  fontSize: '14px',
  border: '1px solid var(--color-error)',
};

const listStyle: CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const listItemStyle: CSSProperties = {
  padding: '16px',
  backgroundColor: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  marginBottom: '12px',
};

const listItemExpiredStyle: CSSProperties = {
  backgroundColor: 'var(--color-badge-warning-bg)',
  borderColor: 'var(--color-error)',
};

const listItemContentStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: '12px',
};

const emailStyle: CSSProperties = {
  margin: 0,
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};

const dateStyle: CSSProperties = {
  margin: '4px 0 0 0',
  fontSize: '14px',
  color: 'var(--color-text-secondary)',
};

const expiredTextStyle: CSSProperties = {
  color: 'var(--color-error)',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const resendButtonStyle: CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
};

const buttonDisabledStyle: CSSProperties = {
  backgroundColor: 'var(--color-text-muted)',
  color: 'var(--color-bg-secondary)',
  cursor: 'not-allowed',
};

const cancelButtonStyle: CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-error)',
  border: '1px solid var(--color-error)',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
};

const expiredBannerStyle: CSSProperties = {
  marginTop: '12px',
  padding: '8px 12px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  borderRadius: '6px',
  fontSize: '14px',
  color: 'var(--color-error)',
};
